import { memo } from 'react'
import { ClockIcon, TagIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import TodayHoursChip from '../TodayHoursChip'
import SightingStreakBadge from '../SightingStreakBadge'
import LiveStatusChip from '../LiveStatusChip'

const DOW_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

function getPeriodKey(hour) {
  if (hour >= 5 && hour <= 11) return 'morning'
  if (hour >= 12 && hour <= 16) return 'afternoon'
  if (hour >= 17 && hour <= 20) return 'evening'
  return 'night'
}

/**
 * EntityPopupInfo - Compact information section for entity popup
 *
 * Displays sighting date, distance/address, and verification status in a
 * minimal single-line layout optimised for small screens.
 *
 * @param {Object} props
 * @param {string} props.variant - 'normal' | 'entity'
 * @param {Object} props.entity - Entity data
 * @param {number} props.confirmationCount - Number of confirmations
 * @param {Function} props.formatSightingDate - Date formatting function
 * @param {number} props.lat - Latitude (entity mode)
 * @param {number} props.lng - Longitude (entity mode)
 * @param {number|null} props.peakDow - 0-6 (Sun-Sat) peak day, or null
 * @param {number|null} props.peakHour - 0-23 peak hour, or null
 *
 * @idempotent - Same props always produce same output
 * @atomic - Single responsibility: render popup info section
 */
const EntityPopupInfo = memo(function EntityPopupInfo({
  variant,
  entity,
  confirmationCount,
  formatSightingDate,
  lat,
  lng,
  peakDow = null,
  peakHour = null,
}) {
  const { t } = useTranslation()
  const sightingDate = entity.createdAt || entity.created_at || entity.lastSeenAt
  const isUnconfirmed = (confirmationCount ?? 0) === 0
  const hasBookableOfferings = !!(entity.hasBookableOfferings ?? entity.has_bookable_offerings)

  // "Usually here" signal — only rendered when the backend supplies sufficient data
  const showUsuallyHere = peakDow != null && peakHour != null
  let usuallyHereLabel = null
  if (showUsuallyHere) {
    const dayName = t(`common.days.${DOW_KEYS[peakDow]}`, DOW_KEYS[peakDow])
    const periodKey = getPeriodKey(peakHour)
    const periodLabel = t(`entities.entity.patterns.periods.${periodKey}`, periodKey + 's')
    usuallyHereLabel = t('entities.entity.patterns.usuallyHere', { day: dayName, period: periodLabel })
  }

  return (
    <div className="space-y-1.5 mb-3">

      {/* Fallback warning — compact banner */}
      {variant === 'normal' && entity.isFallbackUnconfirmed && (
        <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>No confirmed sightings in 72h</span>
        </div>
      )}

      {/* Sighting date + verification status on one row */}
      {sightingDate && (
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>{formatSightingDate(sightingDate)}</span>
          </div>

          {/* Verification pill — right-aligned */}
          {isUnconfirmed ? (
            <span className="flex items-center gap-0.5 text-[11px] text-amber-600 font-medium whitespace-nowrap">
              <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {variant === 'entity' ? 'Needs confirmation' : 'Unverified'}
            </span>
          ) : (
            <span className="flex items-center gap-0.5 text-[11px] text-green-600 font-medium whitespace-nowrap">
              <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {confirmationCount} confirmed
            </span>
          )}
        </div>
      )}

      {/* "Usually here" community-derived peak visit signal */}
      {usuallyHereLabel && (
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <ClockIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          <span>{usuallyHereLabel}</span>
        </div>
      )}

      {/* Services available — shown when entity has at least one bookable priced offering */}
      {hasBookableOfferings && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-accent/10 text-accent">
          <TagIcon className="w-3 h-3 flex-shrink-0" />
          {t('bookings.servicesAvailable', 'Services available')}
        </span>
      )}

      {/* Community-reported open/closed status */}
      {entity.currentEntityStatus && (
        <span className={`self-start text-xs font-medium px-2 py-0.5 rounded-full ${
          entity.currentEntityStatus === 'open'
            ? 'bg-green-100 text-green-700'
            : entity.currentEntityStatus === 'closed'
              ? 'bg-red-100 text-red-700'
              : 'bg-gray-100 text-gray-500'
        }`}>
          {entity.currentEntityStatus === 'open'
            ? t('sightings.status.open', 'Open')
            : entity.currentEntityStatus === 'closed'
              ? t('sightings.status.closed', 'Closed')
              : t('sightings.status.unknown', 'Unknown')}
        </span>
      )}

      {/* Today's hours */}
      {(entity.todayOpenTime || entity.today_open_time) && (
        <TodayHoursChip
          todayOpenTime={entity.todayOpenTime || entity.today_open_time}
          todayCloseTime={entity.todayCloseTime || entity.today_close_time}
        />
      )}

      {/* Live status */}
      <LiveStatusChip
        isLive={entity.isLive ?? entity.is_live ?? false}
        liveUntil={entity.liveUntil || entity.live_until}
      />

      {/* Sighting streak / activity */}
      <SightingStreakBadge
        streakDays={entity.streakDays ?? entity.streak_days ?? 0}
        sightingsWeek={entity.sightingsWeek ?? entity.sightings_week ?? 0}
      />

      {/* Distance — normal mode */}
      {variant === 'normal' && entity.distance > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span>{(entity.distance / 1000).toFixed(1)} km away</span>
        </div>
      )}

      {/* Address with directions link — entity mode */}
      {variant === 'entity' && lat && lng && (
        <div className="flex items-center gap-1.5 text-xs min-w-0">
          <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline flex items-center gap-1 min-w-0"
          >
            <span className="truncate">{entity.currentAddress || entity.current_address || `${lat.toFixed(4)}, ${lng.toFixed(4)}`}</span>
            <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      )}

      {/* Entity mode extras — notes, photo, spotted-by */}
      {variant === 'entity' && (
        <>
          {entity.notes && (
            <p className="text-xs text-gray-600 bg-gray-50 rounded px-2 py-1 leading-snug">
              <span className="font-medium text-gray-700">Note: </span>{entity.notes}
            </p>
          )}

          {entity.photoUrl && (
            <div className="rounded-lg overflow-hidden border border-gray-200/50">
              <img
                src={entity.photoUrl}
                alt="Sighting"
                className="w-full h-24 object-cover"
              />
            </div>
          )}

          {entity.username && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>by <span className="font-medium text-gray-700">{entity.username}</span></span>
            </div>
          )}
        </>
      )}
    </div>
  )
})

export default EntityPopupInfo
